import React, { createContext, useContext, useState } from "react";
import splitFilesIntoChunks from "utilities/splitIntoChunks";

const FileUploadContext = createContext<any | null>(null);

export const FileUploadProvider = ({ children }: any) => {
  const [showUploader, setShowUploader] = useState(false);
  const [fileDetails, setFileDetails] = useState<any>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [totalUpload, setTotalUpload] = useState(0);
  const [filesUploaded, setFilesUploaded] = useState(0);
  const [totalFilesAmount, setTotalFilesAmount] = useState(0);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [filesWithErrors, setFilesWithErrors] = useState<any>([]);
  const [uploaderMessage, setUploaderMessage] = useState({ type: "none", message: "" });
  const [fileType, setFileType] = useState<string | null>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const onFileAbort = () => {
    if (abortController) {
      abortController.abort();
      setUploaderMessage({ type: "error", message: "Upload aborted" });
    }
    setFileDetails(null);
    setFileLoading(false);
    setShowProgressBar(false);
    setIsUploading(false);
  };

  const handleProgressReset = () => {
    setTotalUpload(0);
    setFilesUploaded(0);
    setTotalFilesAmount(0);
    setIsUploading(false);
  };

  const handleFile = (filesUploaded: any) => {
    if (!filesUploaded && !fileDetails?.fileName) {
      setFileDetails(null);
    } else if (filesUploaded) {
      setTotalFilesAmount(filesUploaded.length);
      setFileDetails(splitFilesIntoChunks(filesUploaded, 2));
    }
  };

  const handleFileSubmit = async (type: any, fileUploaderApiConfig: any) => {
    setFilesUploaded(0);
    setFileLoading(true);
    setShowProgressBar(true);
    setIsUploading(true);

    const controller = new AbortController();
    setAbortController(controller);

    try {
      let filesUploadedSoFar = 0;
      for (const fileBundle of fileDetails) {
        if (!fileDetails) break;

        const processedFiles = Object.values(fileBundle);
        const fileNames = processedFiles.map((file: any) => file.name);
        const formData = new FormData();
        processedFiles.forEach((file: any) => formData.append("file", file));

        try {
          const response = type === "volume"
            ? await fileUploaderApiConfig.uploadVolumesForPointClouds(formData, { signal: controller.signal })
            : await fileUploaderApiConfig.uploadImagesForPointClouds(formData, { signal: controller.signal });

          if (response.status === 200) {
            filesUploadedSoFar += processedFiles.length;
            const newProgress = (filesUploadedSoFar / totalFilesAmount) * 100;
            setFilesUploaded(filesUploadedSoFar);
            setTotalUpload(newProgress);
          } else {
            throw new Error("Upload failed");
          }
        } catch (error: any) {
          if (error.name === "AbortError") break;
          setFilesWithErrors((prev: any) => [...prev, ...fileNames]);
        }
      }
    } catch (error) {
      setShowProgressBar(false);
      setIsUploading(false); // Reset upload status on error
    } finally {
      setFileLoading(false);
      setFileType(null);
      setAbortController(null);
      setIsUploading(false); // Reset upload status when done
    }
  };

  return (
    <FileUploadContext.Provider value={{
      fileDetails,
      fileLoading,
      totalUpload,
      filesUploaded,
      totalFilesAmount,
      showProgressBar,
      filesWithErrors,
      onFileAbort,
      handleProgressReset,
      handleFile,
      fileType,
      setFileType,
      handleFileSubmit,
      setFileDetails,
      showUploader,
      setShowUploader,
      uploaderMessage,
      setUploaderMessage,
      setFileLoading,
      setTotalUpload,
      setFilesUploaded,
      setTotalFilesAmount,
      setShowProgressBar,
      setFilesWithErrors,
      isUploading,
      setIsUploading
    }}>
      {children}
    </FileUploadContext.Provider>
  );
};

export const useFileUpload = () => useContext(FileUploadContext);